import { createContext, useCallback, useEffect, useState } from 'react';
import type { User, LoginRequest, RegisterRequest } from '@/types/api';
import { login as loginApi, register as registerApi, getCurrentUser } from '../api/auth-api';
import { getToken, setToken, removeToken, setStoredUser, getStoredUser } from '@/lib/auth';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (data: LoginRequest) => Promise<{ success: boolean; error?: string }>;
  register: (data: RegisterRequest) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  /**
   * Re-fetches `/api/auth/me` and updates the in-memory + cached user object.
   * Used after `change-password` so the route guard sees the cleared
   * `mustChangePassword` flag without requiring a full reload.
   */
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      // Try to get user from storage first
      const storedUser = getStoredUser();
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }

      // Verify with API
      const response = await getCurrentUser();
      if (response.success && response.data) {
        setUser(response.data);
        setStoredUser(JSON.stringify(response.data));
      } else {
        removeToken();
        setUser(null);
      }
    } catch {
      removeToken();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (data: LoginRequest) => {
    try {
      const response = await loginApi(data);
      if (response.success && response.data) {
        setToken(response.data.token);
        setUser(response.data.user);
        setStoredUser(JSON.stringify(response.data.user));
        return { success: true };
      }
      return { success: false, error: response.message || 'Login failed' };
    } catch (error) {
      const message = (error as any)?.response?.data?.message || 'An error occurred during login';
      return { success: false, error: message };
    }
  };

  const register = async (data: RegisterRequest) => {
    try {
      const response = await registerApi(data);
      if (response.success) {
        return { success: true };
      }
      return { success: false, error: response.message || 'Registration failed' };
    } catch (error) {
      return { success: false, error: 'An error occurred during registration' };
    }
  };

  const logout = () => {
    removeToken();
    setUser(null);
  };

  const refreshUser = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    const response = await getCurrentUser();
    if (response.success && response.data) {
      setUser(response.data);
      setStoredUser(JSON.stringify(response.data));
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        register,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
